import type { ReactNode } from 'react';
import { cn } from '@/shared/utils';

interface TableCardProps {
  children: ReactNode;
  className?: string;
}

/**
 * Card wrapper for tables matching the prototype design.
 * Includes rounded corners, border, and shadow.
 * Uses w-full to ensure consistent width.
 */
export function TableCard({ children, className }: TableCardProps) {
  return (
    <div
      className={cn(
        'w-full bg-card rounded-xl border border-border shadow-sm transition-colors duration-300',
        className
      )}
    >
      {children}
    </div>
  );
}

interface TableCardHeaderProps {
  children: ReactNode;
  className?: string;
}

/**
 * Header section of TableCard for search/filter controls.
 */
export function TableCardHeader({ children, className }: TableCardHeaderProps) {
  return (
    <div
      className={cn(
        'p-4 border-b border-border flex flex-col md:flex-row gap-4 items-center justify-between',
        className
      )}
    >
      {children}
    </div>
  );
}

interface TableCardContentProps {
  children: ReactNode;
  className?: string;
}

/**
 * Content section of TableCard for the actual table.
 */
export function TableCardContent({ children, className }: TableCardContentProps) {
  return <div className={cn('overflow-x-auto', className)}>{children}</div>;
}

interface TableCardFooterProps {
  children: ReactNode;
  className?: string;
}

/**
 * Footer section of TableCard for pagination.
 */
export function TableCardFooter({ children, className }: TableCardFooterProps) {
  return (
    <div
      className={cn(
        'p-4 border-t border-border flex items-center justify-between bg-card rounded-b-xl',
        className
      )}
    >
      {children}
    </div>
  );
}
